import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { supabase } from '../../lib/supabase';
import { calcularSlotsDisponibles, type Intervalo } from '../../lib/disponibilidad';
import { DIAS_SEMANA, parseFechaLocal } from '../../lib/horarios';
import type {
  CategoriaServicioRow,
  ProfesionalRow,
  SalonRow,
  ServicioRow,
} from '../../types/database.types';

// El rol `anon` (este portal, sin login) solo tiene permiso de columna
// sobre id/salon_id/nombre/estado de `profesionales` (ver
// fix_seguridad_portal_publico.sql) -- por eso el select de más abajo
// pide solo esas 4 columnas, y por eso este archivo usa este tipo
// recortado (Pick de ProfesionalRow) en vez del `ProfesionalRow`
// completo para todo lo que venga de esa consulta. Usar ProfesionalRow
// completo aquí compilaba antes solo porque el colapso a `never` (ver
// database.types.ts) ocultaba el desajuste de forma.
type ProfesionalPublico = Pick<ProfesionalRow, 'id' | 'salon_id' | 'nombre' | 'estado'>;

/**
 * Portal público de reservas (DAF v2, sección 32 y flujo de la sección 11):
 * categoría → servicio → profesional → fecha/hora → datos de la clienta →
 * confirmar. Sin login — accesible por cualquier persona en /portal.
 *
 * Seguridad: esta página solo puede leer lo que las políticas RLS para el
 * rol `anon` permiten (schema.sql, sección 13) — categorías/servicios
 * activos y disponibles online, profesionales activas, sus horarios, y
 * bloqueos. NO puede leer `citas` directamente (por eso existe el RPC
 * `horarios_ocupados_publico`, que expone solo inicio/fin sin datos de
 * clienta). La reserva en sí se crea con el RPC `crear_reserva_publica`,
 * que hace TODA la validación real en el servidor — lo que este archivo
 * calcula en el cliente (vía `calcularSlotsDisponibles`) es únicamente
 * para no ofrecerle a la clienta una hora que el servidor va a rechazar.
 *
 * Simplificación consciente: como hoy solo existe un salón activo, este
 * portal toma el primero que encuentra. El día de mañana con varios
 * salones (DAF v2, sección 34), esta pantalla necesitaría identificar el
 * salón por una ruta propia (p. ej. /portal/:slug) — se deja anotado aquí
 * en vez de sobre-construir para un escenario que todavía no existe.
 *
 * También asume que el navegador de la clienta está en la zona horaria de
 * Bogotá (igual que `calcularSlotsDisponibles`, ver ese archivo) — una
 * asunción razonable para un salón local con clientela local.
 */

type Paso = 'categoria' | 'servicio' | 'profesional' | 'fecha' | 'datos' | 'confirmado';

interface DatosClienta {
  nombre: string;
  apellido: string;
  telefono: string;
  email: string;
}

function hoyLocalISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Parsea 'YYYY-MM-DD' como fecha LOCAL (no UTC) para evitar que el día se
 * corra al convertir a la zona horaria del navegador. */
function formatoHora(d: Date): string {
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function soloDigitos(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Sistema de color por línea de servicio, confirmado y cerrado con el
 * cliente (`claude/guia-de-marca-enlace.md`): Pestañas es una línea
 * propia con identidad negro/blanco, más gráfica; Uñas y Cabello (y
 * cualquier categoría nueva que el admin cree — DAF v2, sección 7) usan
 * la identidad dorada general de la marca. Como el nombre de categoría
 * es texto libre (el admin puede escribirlo con o sin tilde, mayúsculas,
 * etc.), se compara normalizado en vez de con un match exacto.
 */
function esLineaPestanas(nombreCategoria: string | undefined): boolean {
  if (!nombreCategoria) return false;
  const normalizado = nombreCategoria
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return normalizado.includes('pestana');
}

function linkWhatsapp(telefonoSalon: string | null, mensaje: string): string | null {
  if (!telefonoSalon) return null;
  let digitos = soloDigitos(telefonoSalon);
  if (digitos.length === 10) digitos = `57${digitos}`; // celular colombiano sin indicativo
  return `https://wa.me/${digitos}?text=${encodeURIComponent(mensaje)}`;
}

export function Portal() {
  const [salon, setSalon] = useState<SalonRow | null>(null);
  const [cargandoSalon, setCargandoSalon] = useState(true);

  const [paso, setPaso] = useState<Paso>('categoria');

  const [categorias, setCategorias] = useState<CategoriaServicioRow[]>([]);
  const [categoria, setCategoria] = useState<CategoriaServicioRow | null>(null);

  const [servicios, setServicios] = useState<ServicioRow[]>([]);
  const [servicio, setServicio] = useState<ServicioRow | null>(null);

  const [profesionales, setProfesionales] = useState<ProfesionalPublico[]>([]);
  const [profesional, setProfesional] = useState<ProfesionalPublico | null>(null);

  const [fecha, setFecha] = useState(hoyLocalISO());
  const [cargandoSlots, setCargandoSlots] = useState(false);
  const [slots, setSlots] = useState<Intervalo[]>([]);
  const [slotElegido, setSlotElegido] = useState<Intervalo | null>(null);

  const [datos, setDatos] = useState<DatosClienta>({ nombre: '', apellido: '', telefono: '', email: '' });
  // Consentimiento (Ley 1581 de 2012 — ver Plantillas_Legales_Salon.md,
  // Parte II): la casilla de tratamiento de datos es obligatoria para
  // reservar; la de marketing es independiente y empieza desmarcada. El
  // servidor (crear_reserva_publica) también exige la primera — este
  // estado solo controla la UI, no es la protección real.
  const [aceptaTratamiento, setAceptaTratamiento] = useState(false);
  const [aceptaMarketing, setAceptaMarketing] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [citaId, setCitaId] = useState<string | null>(null);

  useEffect(() => {
    supabase
      .from('salones')
      .select('*')
      .eq('estado', 'activo')
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setSalon(data);
        setCargandoSalon(false);
      });
  }, []);

  useEffect(() => {
    if (!salon) return;
    supabase
      .from('categorias_servicios')
      .select('*')
      .eq('salon_id', salon.id)
      .eq('estado', 'activo')
      .order('nombre')
      .then(({ data }) => setCategorias(data ?? []));
  }, [salon]);

  function elegirCategoria(c: CategoriaServicioRow) {
    setCategoria(c);
    setServicio(null);
    setProfesional(null);
    setPaso('servicio');
    if (!salon) return;
    supabase
      .from('servicios')
      .select('*')
      .eq('salon_id', salon.id)
      .eq('categoria_id', c.id)
      .eq('estado', 'activo')
      .eq('disponible_online', true)
      .order('nombre')
      .then(({ data }) => setServicios(data ?? []));
  }

  async function elegirServicio(s: ServicioRow) {
    setServicio(s);
    setProfesional(null);
    setPaso('profesional');
    if (!salon) return;

    const { data: ps } = await supabase.from('profesional_servicios').select('profesional_id').eq('servicio_id', s.id);
    const ids = (ps ?? []).map((r) => r.profesional_id);
    if (ids.length === 0) {
      setProfesionales([]);
      return;
    }
    const { data: profs } = await supabase
      .from('profesionales')
      // Solo estas 4 columnas: el rol anon (portal público, sin login)
      // ya no tiene permiso de columna sobre documento/telefono/whatsapp/
      // correo de la profesional (ver fix_seguridad_portal_publico.sql) —
      // un select('*') aquí fallaría con "permission denied for column".
      .select('id, salon_id, nombre, estado')
      .eq('salon_id', salon.id)
      .eq('estado', 'activo')
      .in('id', ids)
      .order('nombre');
    setProfesionales(profs ?? []);
  }

  function elegirProfesional(p: ProfesionalPublico) {
    setProfesional(p);
    setSlotElegido(null);
    setPaso('fecha');
  }

  useEffect(() => {
    if (paso !== 'fecha' || !profesional || !servicio || !salon) return;
    cargarSlots();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paso, profesional, servicio, salon, fecha]);

  async function cargarSlots() {
    if (!profesional || !servicio || !salon) return;
    setCargandoSlots(true);
    setSlotElegido(null);

    const fechaLocal = parseFechaLocal(fecha);
    const diaSemana = fechaLocal.getDay();

    const [{ data: horarios }, { data: bloqueosRaw }, { data: ocupadosRaw }] = await Promise.all([
      supabase.from('horarios_profesionales').select('*').eq('profesional_id', profesional.id).eq('dia_semana', diaSemana),
      // anon ya no lee bloqueos_horarios directo (esa tabla tiene
      // `motivo` y `usuario_id`, notas internas del staff) — este RPC
      // security definer expone solo inicio/fin, igual que
      // horarios_ocupados_publico hace para citas. Ver
      // fix_seguridad_portal_publico.sql.
      supabase.rpc('bloqueos_ocupados_publico', {
        p_salon_id: salon.id,
        p_profesional_id: profesional.id,
        p_fecha: fecha,
      }),
      supabase.rpc('horarios_ocupados_publico', { p_profesional_id: profesional.id, p_fecha: fecha }),
    ]);

    if (!horarios || horarios.length === 0) {
      setSlots([]);
      setCargandoSlots(false);
      return;
    }

    const bloqueos: Intervalo[] = (bloqueosRaw ?? []).map((b) => ({ inicio: new Date(b.inicio), fin: new Date(b.fin) }));
    const citasExistentes: Intervalo[] = (ocupadosRaw ?? []).map((c) => ({ inicio: new Date(c.inicio), fin: new Date(c.fin) }));

    // Puede haber más de un rango horario para el mismo día (por ahora
    // schema.sql permite varias filas por día); se calculan slots para
    // cada rango y se combinan.
    const todos = horarios.flatMap((h) =>
      calcularSlotsDisponibles({
        fecha: fechaLocal,
        horaInicioLaboral: h.hora_inicio.slice(0, 5),
        horaFinLaboral: h.hora_fin.slice(0, 5),
        duracionServicioMinutos: servicio.duracion_minutos,
        citasExistentes,
        bloqueos,
        // DAF v2 / recomendaciones costo $0, sección 12: anticipación
        // mínima configurable por salón — no ofrecer en el cliente una
        // hora que el servidor va a rechazar (crear_reserva_publica
        // aplica la misma regla, ver schema_anticipacion_minima.sql).
        anticipacionMinimaMinutos: salon.anticipacion_minima_minutos,
      })
    );
    todos.sort((a, b) => a.inicio.getTime() - b.inicio.getTime());
    setSlots(todos);
    setCargandoSlots(false);
  }

  function continuarADatos() {
    if (!slotElegido) return;
    setPaso('datos');
  }

  async function confirmarReserva() {
    if (!salon || !servicio || !profesional || !slotElegido) return;
    setEnviando(true);
    setError(null);

    const { data, error: err } = await supabase.rpc('crear_reserva_publica', {
      p_salon_id: salon.id,
      p_servicio_id: servicio.id,
      p_profesional_id: profesional.id,
      p_inicio: slotElegido.inicio.toISOString(),
      p_nombre_cliente: datos.nombre,
      p_apellido_cliente: datos.apellido,
      p_telefono: datos.telefono,
      p_email: datos.email || null,
      p_acepto_tratamiento_datos: aceptaTratamiento,
      p_acepto_marketing: aceptaMarketing,
    });

    setEnviando(false);

    if (err) {
      setError(traducirError(err.message));
      return;
    }

    setCitaId(data);
    setPaso('confirmado');
  }

  function traducirError(msg: string): string {
    if (msg.includes('Horario fuera del horario laboral')) return 'Esa hora ya no está disponible. Elige otra, por favor.';
    if (msg.includes('bloqueado')) return 'Ese horario acaba de bloquearse. Elige otra hora, por favor.';
    if (msg.includes('fechas u horas pasadas')) return 'Esa hora ya pasó. Elige otra, por favor.';
    if (msg.includes('anticipación')) return 'Esa hora está demasiado próxima. Elige una hora con un poco más de anticipación, por favor.';
    if (msg.includes('excl') || msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('conflict')) {
      return 'Justo se acaba de reservar esa hora con otra clienta. Elige otra, por favor.';
    }
    return 'No se pudo completar la reserva. Intenta de nuevo o escríbenos por WhatsApp.';
  }

  const mensajeWhatsapp = useMemo(() => {
    if (!servicio || !profesional || !slotElegido) return '';
    const fechaTxt = slotElegido.inicio.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
    return `Hola, soy ${datos.nombre} ${datos.apellido}. Acabo de reservar ${servicio.nombre} con ${profesional.nombre} el ${fechaTxt} a las ${formatoHora(slotElegido.inicio)}. ¡Gracias!`;
  }, [servicio, profesional, slotElegido, datos]);

  // Una vez elegida la categoría, el resto del flujo (servicio → fecha →
  // confirmación) hereda su línea de color — ver esLineaPestanas arriba.
  const accentoNegro = esLineaPestanas(categoria?.nombre);

  if (cargandoSalon) {
    return <PantallaCentrada>Cargando…</PantallaCentrada>;
  }

  if (!salon) {
    return <PantallaCentrada>Este salón no está disponible para reservas en este momento.</PantallaCentrada>;
  }

  return (
    <div className="min-h-screen bg-cream">
      <header className="border-b border-onyx/10 bg-onyx px-4 py-6 text-center">
        <p className="font-display text-2xl tracking-wide text-gold-light">{salon.nombre}</p>
        <p className="mt-1 text-sm text-cream/70">Reserva tu cita en línea</p>
      </header>

      <main className="mx-auto max-w-lg px-4 py-8">
        <Pasos actual={paso} />

        {paso === 'categoria' && (
          <PasoLista titulo="¿Qué te gustaría reservar?">
            {categorias.length === 0 && <p className="text-sm text-onyx/50">No hay categorías disponibles por ahora.</p>}
            {categorias.map((c) => (
              <BotonOpcion key={c.id} onClick={() => elegirCategoria(c)} accento={esLineaPestanas(c.nombre) ? 'negro' : 'gold'}>
                {c.nombre}
              </BotonOpcion>
            ))}
          </PasoLista>
        )}

        {paso === 'servicio' && (
          <PasoLista titulo={`Elige el servicio${categoria ? ` — ${categoria.nombre}` : ''}`} onVolver={() => setPaso('categoria')}>
            {servicios.length === 0 && (
              <p className="text-sm text-onyx/50">No hay servicios disponibles para reserva online en esta categoría.</p>
            )}
            {servicios.map((s) => (
              <BotonOpcion key={s.id} onClick={() => elegirServicio(s)} accento={accentoNegro ? 'negro' : 'gold'}>
                <div className="flex items-center justify-between">
                  <span>{s.nombre}</span>
                  <span className="text-xs text-onyx/50">
                    {s.duracion_minutos} min · ${s.precio.toLocaleString('es-CO')}
                  </span>
                </div>
              </BotonOpcion>
            ))}
          </PasoLista>
        )}

        {paso === 'profesional' && (
          <PasoLista titulo="Elige tu profesional" onVolver={() => setPaso('servicio')}>
            {profesionales.length === 0 && (
              <p className="text-sm text-onyx/50">No hay profesionales disponibles para este servicio por ahora.</p>
            )}
            {profesionales.map((p) => (
              <BotonOpcion key={p.id} onClick={() => elegirProfesional(p)} accento={accentoNegro ? 'negro' : 'gold'}>
                {p.nombre}
              </BotonOpcion>
            ))}
          </PasoLista>
        )}

        {paso === 'fecha' && (
          <PasoLista titulo="Elige fecha y hora" onVolver={() => setPaso('profesional')}>
            <label className="block">
              <span className="mb-1 block text-sm font-medium text-onyx/80">Fecha</span>
              <input
                type="date"
                className="input-field"
                min={hoyLocalISO()}
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
              />
            </label>

            <div className="mt-4">
              {cargandoSlots && <p className="text-sm text-onyx/50">Buscando horas disponibles…</p>}
              {!cargandoSlots && slots.length === 0 && (
                <p className="text-sm text-onyx/50">
                  No hay horas disponibles el {DIAS_SEMANA[parseFechaLocal(fecha).getDay()].toLowerCase()}. Prueba otra
                  fecha.
                </p>
              )}
              {!cargandoSlots && slots.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {slots.map((s) => (
                    <button
                      key={s.inicio.toISOString()}
                      type="button"
                      onClick={() => setSlotElegido(s)}
                      className={`rounded-md border px-2 py-2 text-sm transition-colors ${
                        slotElegido?.inicio.getTime() === s.inicio.getTime()
                          ? accentoNegro
                            ? 'border-onyx bg-onyx text-cream'
                            : 'border-gold bg-gold text-cream'
                          : accentoNegro
                            ? 'border-onyx/15 bg-white text-onyx hover:bg-onyx/10'
                            : 'border-onyx/15 bg-white text-onyx hover:bg-gold-light/20'
                      }`}
                    >
                      {formatoHora(s.inicio)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button type="button" disabled={!slotElegido} onClick={continuarADatos} className="btn-primary mt-6 w-full">
              Continuar
            </button>
          </PasoLista>
        )}

        {paso === 'datos' && (
          <PasoLista titulo="Tus datos" onVolver={() => setPaso('fecha')}>
            <ResumenReserva servicio={servicio} profesional={profesional} slot={slotElegido} accentoNegro={accentoNegro} />

            <div className="mt-4 space-y-3">
              <Campo label="Nombre" required>
                <input
                  required
                  className="input-field"
                  value={datos.nombre}
                  onChange={(e) => setDatos((d) => ({ ...d, nombre: e.target.value }))}
                />
              </Campo>
              <Campo label="Apellido" required>
                <input
                  required
                  className="input-field"
                  value={datos.apellido}
                  onChange={(e) => setDatos((d) => ({ ...d, apellido: e.target.value }))}
                />
              </Campo>
              <Campo label="Teléfono / WhatsApp" required>
                <input
                  required
                  type="tel"
                  className="input-field"
                  value={datos.telefono}
                  onChange={(e) => setDatos((d) => ({ ...d, telefono: e.target.value }))}
                />
              </Campo>
              <Campo label="Correo (opcional)">
                <input
                  type="email"
                  className="input-field"
                  value={datos.email}
                  onChange={(e) => setDatos((d) => ({ ...d, email: e.target.value }))}
                />
              </Campo>
            </div>

            <div className="mt-4 space-y-2 border-t border-onyx/10 pt-4 text-sm text-onyx/80">
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={aceptaTratamiento}
                  onChange={(e) => setAceptaTratamiento(e.target.checked)}
                />
                <span>
                  He leído y acepto la{' '}
                  <a
                    href="/portal/politica-datos"
                    target="_blank"
                    rel="noreferrer"
                    className="text-gold-deep underline"
                  >
                    Política de Tratamiento de Datos Personales
                  </a>{' '}
                  y los{' '}
                  <a href="/portal/terminos" target="_blank" rel="noreferrer" className="text-gold-deep underline">
                    Términos y Condiciones
                  </a>
                  . Autorizo el uso de mis datos de contacto para gestionar mi cita.
                  <span className="text-gold-deep"> *</span>
                </span>
              </label>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={aceptaMarketing}
                  onChange={(e) => setAceptaMarketing(e.target.checked)}
                />
                <span>También quiero recibir novedades y promociones por WhatsApp o correo (opcional).</span>
              </label>
            </div>

            {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

            <button
              type="button"
              disabled={enviando || !datos.nombre || !datos.apellido || !datos.telefono || !aceptaTratamiento}
              onClick={confirmarReserva}
              className="btn-primary mt-6 w-full"
            >
              {enviando ? 'Confirmando…' : 'Confirmar reserva'}
            </button>
          </PasoLista>
        )}

        {paso === 'confirmado' && (
          <div className="card text-center">
            <p className="mb-2 text-3xl">✓</p>
            <h2 className="mb-2 font-display text-xl text-onyx">¡Reserva confirmada!</h2>
            <ResumenReserva servicio={servicio} profesional={profesional} slot={slotElegido} accentoNegro={accentoNegro} />
            <p className="mt-4 text-sm text-onyx/60">
              Te esperamos. Si necesitas cancelar o cambiar la hora, escríbenos por WhatsApp.
            </p>
            {citaId && (() => {
              const link = linkWhatsapp(salon.telefono, mensajeWhatsapp);
              return link ? (
                <a
                  href={link}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary mt-4 inline-flex bg-[#25D366] hover:bg-[#1DA851]"
                >
                  Confirmar por WhatsApp
                </a>
              ) : null;
            })()}
          </div>
        )}
      </main>
    </div>
  );
}

function PantallaCentrada({ children }: { children: ReactNode }) {
  return <div className="flex min-h-screen items-center justify-center bg-cream text-onyx/60">{children}</div>;
}

function Pasos({ actual }: { actual: Paso }) {
  const orden: Paso[] = ['categoria', 'servicio', 'profesional', 'fecha', 'datos', 'confirmado'];
  const indice = orden.indexOf(actual);
  return (
    <div className="mb-6 flex justify-center gap-1.5">
      {orden.map((p, i) => (
        <span key={p} className={`h-1.5 w-6 rounded-full ${i <= indice ? 'bg-gold' : 'bg-onyx/10'}`} />
      ))}
    </div>
  );
}

function PasoLista({
  titulo,
  onVolver,
  children,
}: {
  titulo: string;
  onVolver?: () => void;
  children: ReactNode;
}) {
  return (
    <div className="card">
      <div className="mb-4 flex items-center gap-2">
        {onVolver && (
          <button type="button" onClick={onVolver} className="text-onyx/40 hover:text-onyx" aria-label="Volver">
            ←
          </button>
        )}
        <h2 className="font-display text-lg text-onyx">{titulo}</h2>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function BotonOpcion({
  onClick,
  children,
  accento = 'gold',
}: {
  onClick: () => void;
  children: ReactNode;
  /** Línea de servicio (guia-de-marca-enlace.md): Pestañas usa acento
   * negro/blanco propio; todo lo demás usa el dorado general de la marca. */
  accento?: 'gold' | 'negro';
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full rounded-md border border-onyx/15 bg-white px-4 py-3 text-left text-sm text-onyx transition-colors ${
        accento === 'negro' ? 'hover:border-onyx hover:bg-onyx/5' : 'hover:border-gold hover:bg-gold-light/10'
      }`}
    >
      {children}
    </button>
  );
}

function Campo({ label, required, children }: { label: string; required?: boolean; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-onyx/80">
        {label}
        {required && <span className="text-gold-deep"> *</span>}
      </span>
      {children}
    </label>
  );
}

function ResumenReserva({
  servicio,
  profesional,
  slot,
  accentoNegro = false,
}: {
  servicio: ServicioRow | null;
  profesional: ProfesionalPublico | null;
  slot: Intervalo | null;
  /** Línea de servicio elegida (ver esLineaPestanas) — un pequeño borde
   * de color a la izquierda, no un rediseño de la tarjeta. */
  accentoNegro?: boolean;
}) {
  if (!servicio || !profesional || !slot) return null;
  const fechaTxt = slot.inicio.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <div
      className={`rounded-md border-l-4 bg-onyx/5 p-3 text-sm text-onyx/80 ${
        accentoNegro ? 'border-l-onyx' : 'border-l-gold'
      }`}
    >
      <p>
        <strong>{servicio.nombre}</strong> con {profesional.nombre}
      </p>
      <p className="capitalize">
        {fechaTxt} · {formatoHora(slot.inicio)}
      </p>
      <p className="mt-1 text-onyx/50">${servicio.precio.toLocaleString('es-CO')}</p>
    </div>
  );
}
